import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  ConversionAnalyticsFilters,
  ConversionAnalyticsMetrics,
  ConversionFunnelStep,
  DateRangePeriod,
} from "../types/analytics";
import { logger } from "../utils/logger";
import {
  getScopedConversionFunnel,
  getScopedConversionMetrics,
} from "../services/api/analytics-router";
import { type ScopedArg, extractArg } from "./participantsAnalyticsSlice";

// ─── Normalizers ──────────────────────────────────────────────────────────────

export const normalizeConversionMetrics = (
  metrics: Partial<ConversionAnalyticsMetrics>,
): ConversionAnalyticsMetrics => ({
  totalVisitors: Number(metrics?.totalVisitors ?? 0),
  totalConversions: Number(metrics?.totalConversions ?? 0),
  overallConversionRate: Number(metrics?.overallConversionRate ?? 0),
  averageConversionTime: Number(metrics?.averageConversionTime ?? 0),
  topPerformingEvent: String(metrics?.topPerformingEvent ?? ""),
  worstPerformingEvent: String(metrics?.worstPerformingEvent ?? ""),
  conversionTrend: Number(metrics?.conversionTrend ?? 0),
});

export const normalizeConversionFilters = (
  filters: ConversionAnalyticsFilters = {},
): ConversionAnalyticsFilters => {
  const result: ConversionAnalyticsFilters = {
    dateRange: filters.dateRange ?? DateRangePeriod.LAST_30,
  };
  if (filters.eventId !== undefined) result.eventId = filters.eventId;
  if (filters.funnelType !== undefined) result.funnelType = filters.funnelType;
  return result;
};

// ─── State ────────────────────────────────────────────────────────────────────

export interface ConversionsAnalyticsState {
  funnel: ConversionFunnelStep[];
  metrics: ConversionAnalyticsMetrics | null;
  filters: ConversionAnalyticsFilters;
  loading: {
    funnel: boolean;
    metrics: boolean;
  };
  error: string | null;
}

const initialState: ConversionsAnalyticsState = {
  funnel: [],
  metrics: null,
  filters: {},
  loading: { funnel: false, metrics: false },
  error: null,
};

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const fetchConversionFunnel = createAsyncThunk(
  "conversions/fetchConversionFunnel",
  async (arg: ScopedArg<ConversionAnalyticsFilters>) => {
    try {
      const { filters, scope } = extractArg(arg, {});
      return getScopedConversionFunnel(scope, filters);
    } catch (error) {
      logger.error("Не удалось получить данные воронки конверсии", error);
      throw error;
    }
  },
);

export const fetchConversionMetrics = createAsyncThunk(
  "conversions/fetchConversionMetrics",
  async (arg: ScopedArg<ConversionAnalyticsFilters>) => {
    try {
      const { filters, scope } = extractArg(arg, {});
      const metrics = await getScopedConversionMetrics(scope, filters);
      return normalizeConversionMetrics(metrics);
    } catch (error) {
      logger.error("Не удалось получить метрики конверсий", error);
      throw error;
    }
  },
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const conversionsAnalyticsSlice = createSlice({
  name: "conversions",
  initialState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(fetchConversionFunnel.pending, (state, action) => {
        state.loading.funnel = true;
        state.error = null;
        if (action.meta.arg) {
          const { filters } = extractArg(action.meta.arg, {});
          state.filters = {
            ...state.filters,
            ...normalizeConversionFilters(filters),
          };
        }
      })
      .addCase(fetchConversionFunnel.fulfilled, (state, action) => {
        state.loading.funnel = false;
        state.funnel = action.payload;
      })
      .addCase(fetchConversionFunnel.rejected, (state, action) => {
        state.loading.funnel = false;
        state.error =
          action.error.message ?? "Ошибка загрузки воронки конверсий";
      })

      .addCase(fetchConversionMetrics.pending, (state) => {
        state.loading.metrics = true;
      })
      .addCase(fetchConversionMetrics.fulfilled, (state, action) => {
        state.loading.metrics = false;
        state.metrics = action.payload;
      })
      .addCase(fetchConversionMetrics.rejected, (state, action) => {
        state.loading.metrics = false;
        state.error =
          action.error.message ?? "Ошибка загрузки метрик конверсий";
      }),
});

export default conversionsAnalyticsSlice.reducer;
